import {
  endOfDay,
  format,
  formatDistanceToNow,
  getTime,
  getUnixTime,
  Interval,
  isBefore,
  isWithinInterval,
  startOfDay,
} from 'date-fns';
import { de } from 'date-fns/locale/de';
// ----------------------------------------------------------------------

type InputValue = Date | string | number | null;

export function fDate(date: InputValue, newFormat?: string) {
  const fm = newFormat || 'dd MMM yyyy';

  return date ? format(new Date(date), fm) : '';
}

export function fDateTime(date: InputValue, newFormat?: string) {
  const fm = newFormat || 'dd MMM yyyy p';

  return date ? format(new Date(date), fm) : '';
}

export function fTimestamp(date: InputValue) {
  return date ? getTime(new Date(date)) : '';
}

export function getDayTimestamp(date: InputValue | undefined): number | null {
  if (date === null || date === undefined || date === '') {
    return null;
  }

  const parsedDate = new Date(date);
  return Number.isNaN(parsedDate.getTime()) ? null : startOfDay(parsedDate).getTime();
}

export function fToNow(date: InputValue) {
  return date
    ? formatDistanceToNow(new Date(date), {
        addSuffix: true,
      })
    : '';
}

/**
 * Returns the unix timestamp for the given date set to end of day (23:59:59).
 * Accepts a Date object, a date string, or a number (timestamp).
 */
export function getUnixTimeEndOfDay(date: Date | string | number): number {
  return getUnixTime(new Date(date).setHours(23, 59, 59));
}

export function epochDaysToFormattedDate(epochDays: string | null | undefined): string {
  if (epochDays == null || epochDays === '') {
    return '';
  }

  const days = parseInt(epochDays, 10);
  if (Number.isNaN(days)) {
    return '';
  }

  // Java's epoch day is the number of days since January 1, 1970
  const milliseconds = days * 24 * 60 * 60 * 1000;
  const date = new Date(milliseconds);
  return format(date, 'dd.MM.yyyy');
}

export function getPreviousMondayOfCurrentDate(currentDate: Date) {
  const day = currentDate.getDay();
  const difference = currentDate.getDate() - day + (day === 0 ? -6 : 1);
  return new Date(currentDate.setDate(difference));
}

export function fDateDe(date: Date | string | number | undefined | null, newFormat?: string) {
  const fm = newFormat || 'dd.MM.yyyy';
  // Do not create a Unix 0-Date but return ''
  if (date !== null && date !== undefined) {
    // avoid errors while manual typing date in
    try {
      return format(new Date(date), fm, { locale: de });
    } catch (error) {
      console.log(error);
      return '';
    }
  }
  return '';
}

export function fDateTimeDe(date: Date | string | number | undefined) {
  if (date !== null && date !== undefined) {
    return format(new Date(date), 'dd.MM.yyyy, HH:mm');
  }
  return '??.??.?? ??:??';
}

export function nullSafeHourMinuteDisplayValue(value: string | null) {
  const result = ' --- ';
  if (value !== undefined && value !== null) {
    return value;
  }
  return result;
}

export function isDateInRange(date: Date, periodStart: Date, periodEnd: Date) {
  const interval: Interval = {
    start: startOfDay(periodStart),
    end: endOfDay(periodEnd),
  };
  return isWithinInterval(date, interval);
}

export function getDate(value: string) {
  // This is a hack because the TimePicker expects a Date object
  return new Date(`1970-01-01T${value}`);
}

export function fStringPercentDe(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') {
    return '---';
  }

  // Remove any whitespace first
  const trimmedValue = value.trim();

  // Check if the value contains a comma (German decimal separator)
  if (trimmedValue.includes(',')) {
    // Split by comma and take only the integer part
    const integerPart = trimmedValue.split(',')[0];
    return `${integerPart} %`;
  }

  // If no comma is present, just return the trimmed value with % suffix
  return `${trimmedValue} %`;
}

export function isDateErrorOnEvents(
  start: Date | null,
  end: Date | null,
  tpEndRecurrence: Date | null | undefined
) {
  return (
    (start && end ? isBefore(new Date(end), new Date(start)) : false) ||
    (start && tpEndRecurrence
      ? isBefore(new Date(endOfDay(tpEndRecurrence)), new Date(start))
      : false)
  );
}
